import React from 'react';
import { Tooltip } from 'antd';
import { Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Spinner } from '@crc-evans/ui-components';
import { ProjectTabTableEmpty } from '../project/ProjectTabTableEmpty';
import './DPRSettingsSparesTab.css';

/**
 * Modern spares grid: row 1 = sortable titles, row 2 = per-column search.
 */
const SparesModernDataGrid = ({
  sortedRows,
  isLoading,
  current,
  pageSize,
  sortKey,
  sortDir,
  onToggleSort,
  formatAssignedToCell,
  formatCostCell,
  totalCostDisplay,
  onEdit,
  onDelete,
  equipmentTypeValue,
  assignedToSerialNumberValue,
  equipmentSubCategory,
  sapKitNo,
  sapNumber,
  description,
  safetyStock,
  cost,
  onServerFilterChange,
  setEquipmentTypeValue,
  setAssignedToSerialNumberValue,
  setEquipmentSubCategory,
  setSapKitNo,
  setSapNumber,
  setDescription,
  setSafetyStock,
  setCost,
  localColFilters,
  onLocalColFilterChange,
  tableWrapClassName = '',
  tableMaxHeight,
}) => {
  const renderSortableHead = (label, colKey, alignLeft) => {
    const active = sortKey === colKey;
    const Icon = active && sortDir === 'desc' ? ChevronDown : ChevronUp;
    return (
      <div
        className={`dpr-settings-spares-cell dpr-settings-spares-sortable ${alignLeft ? 'dpr-settings-spares-cell--left' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => onToggleSort(colKey)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleSort(colKey);
          }
        }}
      >
        <span>{label}</span>
        <Icon
          className={`dpr-settings-spares-sort-icon ${active ? 'dpr-settings-spares-sort-icon-active' : ''}`}
          size={14}
          aria-hidden
        />
      </div>
    );
  };

  const serverFilter = (value, filterType, setter) => (
    <input
      type="text"
      className="dpr-settings-spares-col-filter"
      placeholder="Search..."
      value={value ?? ''}
      onChange={(e) => onServerFilterChange(filterType, e.target.value, setter)}
      aria-label={`Filter ${filterType}`}
    />
  );

  const localFilter = (field) => (
    <input
      type="text"
      className="dpr-settings-spares-col-filter"
      placeholder="Search..."
      value={localColFilters[field] ?? ''}
      onChange={(e) => onLocalColFilterChange(field, e.target.value)}
      aria-label={`Filter ${field}`}
    />
  );

  const filterSpacer = <div className="dpr-settings-spares-col-filter-spacer" aria-hidden />;

  return (
    <div
      className={`dpr-settings-spares-table-shell ${tableWrapClassName}`.trim()}
      style={tableMaxHeight ? { maxHeight: tableMaxHeight } : undefined}
    >
      <div className="dpr-settings-spares-hscroll">
        <div className="dpr-settings-spares-hscroll-inner">
          <div className="dpr-settings-spares-thead">
            <div className="dpr-settings-spares-header-row dpr-settings-spares-header-row--labels">
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">#</div>
              {renderSortableHead('Equipment type', 'equipmentType', true)}
              {renderSortableHead('Assigned to', 'assignedToSerialNumber', true)}
              {renderSortableHead('Sub category', 'subCategory', true)}
              {renderSortableHead('SAP kit no', 'sapKitNo', true)}
              {renderSortableHead('SAP number', 'sapNumber', true)}
              {renderSortableHead('Description', 'description', true)}
              {renderSortableHead('Target stock', 'targetStock', false)}
              {renderSortableHead('Cost', 'cost', false)}
              {renderSortableHead('Pending', 'pendingStock', false)}
              {renderSortableHead('Actual', 'actualStock', false)}
              {renderSortableHead('Consumed', 'consumedQty', false)}
              {renderSortableHead('Total cost', 'totalCost', false)}
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell-pub">Pub.</div>
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell-actions">Actions</div>
            </div>
            <div className="dpr-settings-spares-header-row dpr-settings-spares-header-row--filters">
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">{filterSpacer}</div>
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left">
                {serverFilter(equipmentTypeValue, 'equipmentType', setEquipmentTypeValue)}
              </div>
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left">
                {serverFilter(assignedToSerialNumberValue, 'assignedToSerialNumber', setAssignedToSerialNumberValue)}
              </div>
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left">
                {serverFilter(equipmentSubCategory, 'subCategory', setEquipmentSubCategory)}
              </div>
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left">
                {serverFilter(sapKitNo, 'sapKitNo', setSapKitNo)}
              </div>
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left">
                {serverFilter(sapNumber, 'sapNumber', setSapNumber)}
              </div>
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left">
                {serverFilter(description, 'description', setDescription)}
              </div>
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                {serverFilter(safetyStock, 'targetStock', setSafetyStock)}
              </div>
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                {serverFilter(cost, 'cost', setCost)}
              </div>
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                {localFilter('pendingStock')}
              </div>
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                {localFilter('actualStock')}
              </div>
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                {localFilter('consumedQty')}
              </div>
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                {localFilter('totalCost')}
              </div>
              <div
                className="dpr-settings-spares-cell dpr-settings-spares-cell-pub dpr-settings-spares-filter-cell--no-input"
                aria-hidden="true"
              />
              <div
                className="dpr-settings-spares-cell dpr-settings-spares-cell-actions dpr-settings-spares-filter-cell--no-input"
                aria-hidden="true"
              />
            </div>
          </div>
          <div className="dpr-settings-spares-body-scroll">
            {isLoading ? (
              <div className="dpr-settings-spares-loading">
                <Spinner size="medium" />
              </div>
            ) : sortedRows.length === 0 ? (
              <div className="dpr-settings-spares-empty">
                <ProjectTabTableEmpty />
              </div>
            ) : (
              <div className="dpr-settings-spares-rows">
                {sortedRows.map((row, index) => (
                  <div key={row.id ?? index} className="dpr-settings-spares-row">
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                      {(current - 1) * pageSize + index + 1}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left" title={row.equipmentType}>
                      {row.equipmentType ?? '—'}
                    </div>
                    <div
                      className="dpr-settings-spares-cell dpr-settings-spares-cell--left"
                      title={formatAssignedToCell(row.assignedToSerialNumber)}
                    >
                      {formatAssignedToCell(row.assignedToSerialNumber)}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left" title={row.subCategory}>
                      {row.subCategory ?? '—'}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left" title={row.sapKitNo}>
                      {row.sapKitNo ?? '—'}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left" title={row.sapNumber}>
                      {row.sapNumber ?? '—'}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left" title={row.description}>
                      {row.description ?? '—'}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                      {row.targetStock ?? '—'}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">{formatCostCell(row)}</div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                      {row.pendingStock ?? 0}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                      {row.actualStock ?? 0}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                      {row.consumedQty ?? 0}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                      {totalCostDisplay(row)}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num dpr-settings-spares-cell-pub">
                      <Tooltip title={row.isPublished ? 'Published' : 'Not published'}>
                        <span className="dpr-settings-spares-published-wrap">
                          <span
                            className={`dpr-settings-spares-published-dot ${
                              row.isPublished
                                ? 'dpr-settings-spares-published-dot--yes'
                                : 'dpr-settings-spares-published-dot--no'
                            }`}
                          />
                        </span>
                      </Tooltip>
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell-actions">
                      <button
                        type="button"
                        className="dpr-settings-spares-action-btn"
                        onClick={() => onEdit(row)}
                        title="Edit"
                        aria-label="Edit spare"
                      >
                        <Pencil size={18} strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        className="dpr-settings-spares-action-btn dpr-settings-spares-action-delete"
                        onClick={() => onDelete(row)}
                        title="Delete"
                        aria-label="Delete spare"
                      >
                        <Trash2 size={18} strokeWidth={1.75} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SparesModernDataGrid;
